import { useCallback, useEffect, useRef } from 'react';
import { usePhotoState } from './PhotoStateContext';
import { PhotoService } from '@/services';
import { PhotoData } from '@/types';
import { useError } from './ErrorContext';

export function usePhotos() {
  const { state, dispatch } = usePhotoState();
  const { handleApiError } = useError();
  const hasAttemptedLoad = useRef(false);

  const loadPhotos = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const photos = await PhotoService.getAll();
      dispatch({ type: 'SET_PHOTOS', payload: photos });
    } catch (error) {
      const message = 'Failed to load photos';
      dispatch({ type: 'SET_ERROR', payload: message });
      handleApiError(error, 'Load photos', loadPhotos);
    }
  }, [dispatch, handleApiError]);

  const createPhoto = useCallback(async (photoData: FormData): Promise<PhotoData> => {
    try {
      const newPhoto = await PhotoService.create(photoData);
      dispatch({ type: 'ADD_PHOTO', payload: newPhoto });
      return newPhoto;
    } catch (error) {
      const message = 'Failed to create photo';
      dispatch({ type: 'SET_ERROR', payload: message });
      handleApiError(error, 'Create photo');
      throw error;
    }
  }, [dispatch, handleApiError]);

  const updatePhoto = useCallback(async (id: string, updates: FormData): Promise<PhotoData> => {
    try {
      const updatedPhoto = await PhotoService.update(id, updates);
      dispatch({ type: 'UPDATE_PHOTO', payload: updatedPhoto });
      return updatedPhoto;
    } catch (error) {
      const message = 'Failed to update photo';
      dispatch({ type: 'SET_ERROR', payload: message });
      handleApiError(error, 'Update photo');
      throw error;
    }
  }, [dispatch, handleApiError]);

  const archivePhoto = useCallback(async (id: string): Promise<void> => {
    try {
      await PhotoService.archive(id);
      // After archiving, reload photos to reflect the change
      await loadPhotos();
    } catch (error) {
      const message = 'Failed to archive photo';
      dispatch({ type: 'SET_ERROR', payload: message });
      handleApiError(error, 'Archive photo');
      throw error;
    }
  }, [dispatch, handleApiError, loadPhotos]);

  const deletePhoto = useCallback(async (id: string): Promise<void> => {
    try {
      await PhotoService.delete(id);
      dispatch({ type: 'DELETE_PHOTO', payload: id });
    } catch (error) {
      const message = 'Failed to delete photo';
      dispatch({ type: 'SET_ERROR', payload: message });
      handleApiError(error, 'Delete photo');
      throw error;
    }
  }, [dispatch, handleApiError]);

  const selectPhoto = useCallback((photo: PhotoData | null) => {
    dispatch({ type: 'SELECT_PHOTO', payload: photo });
  }, [dispatch]);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, [dispatch]);

  // Auto-load photos on mount
  useEffect(() => {
    if (state.photos.length === 0 && !state.loading && !hasAttemptedLoad.current) {
      hasAttemptedLoad.current = true;
      loadPhotos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.photos.length, state.loading]);

  return {
    photos: state.photos,
    selectedPhoto: state.selectedPhoto,
    loading: state.loading,
    error: state.error,
    loadPhotos,
    createPhoto,
    updatePhoto,
    archivePhoto,
    deletePhoto,
    selectPhoto,
    clearError,
  };
}